import { HttpErrorResponse } from '@angular/common/http';
import { Component, inject, ChangeDetectionStrategy, signal } from '@angular/core';
import { Entity } from '../../entity/entity';
import { PartSearchResult } from '../../products/part-search-result';
import { EntityService } from '../../services/entity-service';
import { PartsService } from '../../services/parts-service';
import { QuoteService } from '../../services/quote-service';
import { StringUtils } from '../../utils/string-utils';
import { MAT_DIALOG_DATA, MatDialogActions, MatDialogRef } from '@angular/material/dialog';
import { Part } from '../../products/part';
import { FileUtils } from '../../utils/file-utils';
import { DialogService } from '../../services/dialog.service';
import { MatProgressSpinner } from '@angular/material/progress-spinner';
import { MatIcon } from '@angular/material/icon';
import { MatButton } from '@angular/material/button';
import { CurrencyPipe } from '@angular/common';
import { FormsModule } from '@angular/forms';

@Component({
  selector: 'app-parts-dialog',
  templateUrl: './parts-dialog.component.html',
  styleUrls: ['./parts-dialog.component.css'],
  changeDetection: ChangeDetectionStrategy.OnPush,
  imports: [FormsModule, MatButton, MatIcon, MatProgressSpinner, MatDialogActions, CurrencyPipe],
})
export class PartsDialogComponent {
  private readonly partsService = inject(PartsService);
  private readonly quoteService = inject(QuoteService);
  private readonly entityService = inject(EntityService);
  dialogRef = inject<MatDialogRef<PartsDialogComponent>>(MatDialogRef);
  private readonly dialogService = inject(DialogService);
  private readonly data = inject(MAT_DIALOG_DATA);
  entity: Entity;
  appEngineerSnBypass: boolean;
  readonly searchTerm = signal<string>(undefined!);
  readonly orderYear = signal<number>(undefined!);
  readonly warranty = signal<boolean>(false);
  readonly warrantyAvailable = signal<boolean>(true);
  readonly searchResult = signal<PartSearchResult>(undefined!);
  readonly errorMessage = signal<string>(undefined!);
  readonly isBusy = signal<boolean>(false);
  warrantyRequestNotes!: string;
  readonly availableDrawings = signal<string[]>(undefined!);

  constructor() {
    this.entity = this.data.entity as Entity;
    this.appEngineerSnBypass = this.data.appEngineerSnBypass === true;
  }

  public saveAndClose(): void {
    this.errorMessage.set(undefined!);
    if (this.appEngineerSnBypass) {
      this.finalizeSaveAndClose();
      return;
    }
    const validSerialNumberParts: Part[] = this.searchResult().parts.filter((part: Part) => +part.quantity === 1 && part.requireSerialNumber);
    const invalidSerialNumberParts: Part[] = this.searchResult().parts.filter((part: Part) => +part.quantity > 1 && part.requireSerialNumber);
    if (invalidSerialNumberParts.length > 0) {
      const pluralizedSubject: string = invalidSerialNumberParts.length === 1 ? 'Part ' : 'Parts ';
      const pluralizedVerb: string = invalidSerialNumberParts.length === 1 ? ' requires ' : ' require ';
      const partNumberList: string =
        invalidSerialNumberParts.length === 1 ? invalidSerialNumberParts[0].partNumber : invalidSerialNumberParts.map((part: Part) => part.partNumber).join(', ');
      this.errorMessage.set(pluralizedSubject + partNumberList + pluralizedVerb + 'a serial number and can only be entered one at a time.');
    } else {
      if (validSerialNumberParts.length > 0) {
        this.dialogService.showPartsSerialNumberDialog(this.searchResult().parts).subscribe({
          next: (updatedParts: Part[]) => {
            if ((updatedParts?.length || 0) > 0) {
              this.searchResult().parts = updatedParts;
              this.finalizeSaveAndClose();
            }
          },
          error: (error: any) => this.handleError(error),
        });
      } else {
        this.finalizeSaveAndClose();
      }
    }
  }

  private finalizeSaveAndClose(): void {
    this.isBusy.set(true);
    this.searchResult().warrantyRequestNotes = this.warrantyRequestNotes;
    if (this.isAddQuote()) {
      this.quoteService.createPartsQuote(this.searchResult()).subscribe({
        next: (result) => {
          this.isBusy.set(false);
          this.dialogRef.close(result);
        },
        error: (error) => this.handleError(error),
      });
    } else {
      this.entityService.addParts(this.entity.id.entityId, this.entity.id.entityRevision, this.searchResult()).subscribe({
        next: (result) => {
          this.isBusy.set(false);
          this.dialogRef.close(result);
        },
        error: (error) => this.handleError(error),
      });
    }
  }

  public cancel(): void {
    this.dialogRef.close(null);
  }

  public warrantyChanged(): void {
    this.searchResult.set(undefined!);
  }

  public search(): void {
    this.isBusy.set(true);
    this.errorMessage.set(undefined!);
    const entityId: string = this.isAddQuote() ? '' : this.entity.id.entityId;
    const entityRevision: number = this.isAddQuote() ? 0 : this.entity.id.entityRevision;
    this.partsService.searchParts(this.searchTerm(), this.warranty(), entityId, entityRevision, this.orderYear()).subscribe({
      next: (result: PartSearchResult) => {
        if (!result) {
          this.errorMessage.set('There was an error searching for parts');
        } else {
          if (this.warranty() && !result.warranty) {
            this.errorMessage.set('Warranty searches are not valid for this ' + this.entity.id.entityName.toLowerCase());
            this.warrantyAvailable.set(false);
          }
          this.loadAvailableDrawings(result.parts);
          this.searchTerm.set(result.searchTerm);
          this.warranty.set(result.warranty);
          this.searchResult.set(result);
          this.isBusy.set(false);
        }
      },
      error: (error: any) => this.handleError(error),
    });
  }

  private loadAvailableDrawings(parts: Part[]): void {
    this.availableDrawings.set([]);
    if (parts && parts.length > 0) {
      for (const part of parts) {
        if (part.partNumber.toUpperCase().startsWith('F501')) {
          this.partsService.isDrawingAvailable(part.partNumber).subscribe({
            next: (result) => {
              if ('TRUE' === result.toUpperCase()) {
                this.availableDrawings.update(current => [...current, part.partNumber]);
              }
            },
          });
        }
      }
    }
  }

  hasDrawing(part: Part): boolean {
    return this.availableDrawings() && this.availableDrawings().indexOf(part.partNumber) >= 0;
  }

  downloadDrawing(part: Part): void {
    this.isBusy.set(true);
    this.partsService.getDrawingFile(part.partNumber).subscribe({
      next: (drawing) => {
        this.isBusy.set(false);
        FileUtils.downloadFile(drawing, part.partNumber + '.pdf');
      },
      error: (error) => this.handleError(error),
    });
  }

  private handleError(error: HttpErrorResponse): void {
    this.isBusy.set(false);
    this.errorMessage.set(StringUtils.getErrorMessage(error));
  }

  public isAddQuote(): boolean {
    return StringUtils.isBlank(this.entity?.id?.entityId || '');
  }

  public noPartsSelected(): boolean {
    if ((this.searchResult()?.parts?.length || 0) === 0) {
      return true;
    } else {
      return this.searchResult().parts.filter((part) => part.quantity > 0).length === 0;
    }
  }

  public openHelpDialog(): void {
    this.dialogService.showPartsHelpDialog();
  }

  cannotCreateQuote(): boolean {
    if (this.noPartsSelected()) {
      return true;
    }
    if (this.warranty()) {
      return !this.warrantyRequestNotes || this.warrantyRequestNotes.trim().length === 0;
    }
    return false;
  }
}
